import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import PropTypes from "prop-types";
import i18n from "../i18n";
import {
  GetUserSettings,
  GetCompanySettings,
} from "../BackendAccess/SettingsHooks";
import { CheckIfLoggedIn } from "BackendAccess/UserHooks";
const defaultUserSettings = {
  theme: "light",
  scale: "default",
  language: "en",
  notificationsEnabled: true,
  startPage: "warehouses",
};

const defaultCompanySettings = {
  currency: "USD",
  timeZone: "UTC",
  language: "en",
};

const SettingsContext = createContext({
  userSettings: defaultUserSettings,
  companySettings: defaultCompanySettings,
  updateUserSettingsLocally: () => {},
  refreshUserSettings: async () => {},
  refreshCompanySettings: async () => {},
});

export const SettingsProvider = ({ children }) => {
  const [userSettings, setUserSettings] = useState(defaultUserSettings);
  const [companySettings, setCompanySettings] = useState(
    defaultCompanySettings
  );

  useEffect(() => {
    refreshUserSettings();
    refreshCompanySettings();
  }, []);

  const refreshUserSettings = useCallback(async () => {
    const [logindata, loginerror] = await CheckIfLoggedIn();
    if (!logindata || (loginerror && loginerror?.errCode !== 0)) {
      return;
    }
    try {
      const [settings, error] = await GetUserSettings();
      let localSettings;
      if (!settings || Object.keys(settings).length === 0) {
        localSettings = defaultUserSettings;
      } else {
        localSettings = { ...defaultUserSettings, ...settings };
        try {
          window.localStorage.setItem(
            "User settings",
            JSON.stringify(localSettings)
          );
        } catch (e) {
          console.warn(`Error writing User settings to localStorage`, e);
        }
      }
      setUserSettings(localSettings);
      i18n.changeLanguage(localSettings.language);
    } catch (error) {
      console.error("Failed to fetch user settings", error);
    }
  }, []);

  const refreshCompanySettings = useCallback(async () => {
    try {
      const [settings, error] = await GetCompanySettings();
      if (!settings || !settings.currency || !settings.language)
        setCompanySettings(defaultCompanySettings);
      else setCompanySettings(settings);
    } catch (error) {
      console.error("Failed to fetch company settings", error);
    }
  }, []);

  const updateUserSettingsLocally = useCallback((updates) => {
    setUserSettings((prev) => ({ ...prev, ...updates }));
    try {
      window.localStorage.setItem(
        "User settings",
        JSON.stringify({ ...userSettings, ...updates })
      );
    } catch (e) {
      console.warn(`Error writing User settings to localStorage`, e);
    }
  }, []);

  return (
    <SettingsContext.Provider
      value={{
        userSettings,
        companySettings,
        updateUserSettingsLocally,
        refreshUserSettings,
        refreshCompanySettings,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

SettingsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }
  return context;
};

export function getCurrencySymbol(code) {
  const symbols = {
    USD: "$",
    EUR: "€",
    GBP: "£",
    JPY: "¥",
    AUD: "A$",
    CAD: "C$",
    CHF: "CHF",
    CNY: "¥",
    SEK: "kr",
    NZD: "$",
    // add more as needed
  };
  return symbols[code] || code;
}

export function formatCurrency(value, code, locale = navigator.language) {
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: code,
    }).format(value);
  } catch (e) {
    return `${getCurrencySymbol(code)}${value}`;
  }
}
